// src/components/CustomerFlow/Steps/DocumentUploadStep.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faImages, 
  faFileAlt,
  faIdCard,
  faIdCardAlt,
  faFileInvoiceDollar,
  faUserCircle,
  faSmile,
  faIdBadge,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import Swal from 'sweetalert2';
import { formatCurrency } from '../../../utils/numberFormatter';
import { getCustomerRouteKey } from '../../../utils/customerRoute';
import { trackMobileDocumentUploadStepEntered, trackMobileDocumentUploaded } from '../../../analytics/events';
import './DocumentUploadStep.css';

const DOCUMENTS_MAP = {
  documentFront: { name: 'DNI Frente', icon: faIdCard },
  documentBack: { name: 'DNI Dorso', icon: faIdCardAlt },
  bankStatement: { name: 'Extracto de Cuenta', icon: faFileInvoiceDollar },
  paycheck: { name: 'Recibo de Sueldo', icon: faFileInvoiceDollar },
  selfie: { name: 'Foto Selfie', icon: faUserCircle },
  selfieSmiling: { name: 'Foto Selfie Sonriendo', icon: faSmile },
  selfieDocument: { name: 'Foto Selfie con Documento', icon: faIdBadge },
  service: { name: 'Servicio', icon: faFileInvoiceDollar },
};

const DocumentUploadStep = ({ 
  flowData, 
  stepStatuses = {},
  onNext,
  searchParams,
  // Props adicionales para uso standalone
  standalone = false,
  tokenScope = null,
  onComplete = null,
  flowVariant = 'initial',
  enableSelfieVerification = false
}) => {
  const [documents, setDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [request, setRequest] = useState(null);
  const [isDocumentsInitialized, setIsDocumentsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [selfieValidationCode, setSelfieValidationCode] = useState(null);
  const [isLoadingSelfieValidationCode, setIsLoadingSelfieValidationCode] = useState(false);
  const hasNotifiedCompletionRef = useRef(false);
  const skipHydratedCompletionRef = useRef(false);

  useEffect(() => {
    initializeStep();
  }, []);

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses?.DocumentUploadStep;
    
    // Si el estado de DocumentUploadStep es pending:
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      // Marcar como auto-activado para evitar re-ejecución
      setHasAutoActivated(true);
      
      // Llamar a la API para marcar el step como in_progress
      if (onNext) {
        onNext({
          step_name: 'DocumentUploadStep',
          step_status: 'in_progress',
          auto_activated: true,
          activation_timestamp: new Date().toISOString()
        }, 'in_progress');
      }
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Track document upload step entered when request is loaded
  useEffect(() => {
    if (request && flowData?.flow_id) {
      const branchId = localStorage.getItem('temp_tokenBranchId');
      const customerDni = request?.customer?.dni;
      trackMobileDocumentUploadStepEntered({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        customerDni: customerDni,
        flowId: flowData?.flow_id
      });
    }
  }, [request, flowData?.flow_id]);

  useEffect(() => {
    const currentStepStatus = stepStatuses?.DocumentUploadStep;
    const isStepAlreadyCompleted = currentStepStatus === 'completed' || currentStepStatus === 'processed';
    
    if (
      isDocumentsInitialized
      && !skipHydratedCompletionRef.current
      && areRequiredDocsUploaded()
      && !isStepAlreadyCompleted
      && !hasNotifiedCompletionRef.current
    ) {
      hasNotifiedCompletionRef.current = true;
      
      if (standalone && onComplete) {
        // Modo standalone: llamar callback personalizado
        onComplete({
          documents_uploaded: true,
          completion_timestamp: new Date().toISOString(),
          documents: documents
        });
      } else if (onNext) {
        // Modo flujo: notificar al flujo que el paso está completado
        onNext({
          step_name: 'DocumentUploadStep',
          step_status: 'completed',
          documents_uploaded: true,
          completion_timestamp: new Date().toISOString()
        }, 'completed');
      }
    }
  }, [documents, isDocumentsInitialized, stepStatuses, standalone, onNext, onComplete]);

  const initializeStep = async () => {
    try {
      setIsLoading(true);
      
      const [, , uploadedTypes] = await Promise.all([
        fetchRequest(),
        fetchSelfieValidationCode(),
        fetchUploadedDocuments()
      ]);
      
      initializeDocuments(uploadedTypes);
      
    } catch (error) {
      console.error('Error initializing document upload step:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveCustomerRequestContext = () => {
    let customerId;
    let requestId;

    if (standalone && tokenScope) {
      customerId = getCustomerRouteKey(tokenScope);
      requestId = tokenScope.request_id || tokenScope.request_id;
    } else if (flowData) {
      customerId = flowData.tokenCustomerId;
      requestId = flowData.tokenRequestId;
    } else {
      throw new Error('No se encontraron datos de cliente o solicitud');
    }

    return { customerId, requestId };
  };

  const fetchUploadedDocuments = async () => {
    try {
      const { customerId, requestId } = resolveCustomerRequestContext();
      const response = await api.get(
        `/customers/${customerId}/requests/${requestId}/documents`
      );
      const payload = response.data;
      const serverDocs = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);

      return new Set(
        serverDocs
          .filter((doc) => doc?.status === 'uploaded' && doc?.type)
          .map((doc) => doc.type)
      );
    } catch (error) {
      console.error('Error fetching uploaded documents:', error);
      return new Set();
    }
  };

  const fetchRequest = async () => {
    try {
      const { customerId, requestId } = resolveCustomerRequestContext();

      const response = await api.get(
        `/customers/${customerId}/requests/${requestId}`
      );
      setRequest(response.data);
    } catch (error) {
      console.error('Error fetching request request:', error);
    }
  };

  const fetchSelfieValidationCode = async () => {
    if (flowVariant !== 'approved' || !enableSelfieVerification) {
      setSelfieValidationCode(null);
      setIsLoadingSelfieValidationCode(false);
      return;
    }

    try {
      setIsLoadingSelfieValidationCode(true);
      const { customerId, requestId } = resolveCustomerRequestContext();

      if (!customerId || !requestId || /^\d+$/.test(String(requestId))) {
        setSelfieValidationCode(null);
        setIsLoadingSelfieValidationCode(false);
        return;
      }

      const response = await api.get(
        `/customers/${customerId}/requests/${requestId}/selfie-verification`
      );

      setSelfieValidationCode(response.data?.data?.code || null);
    } catch (error) {
      console.error('Error fetching selfie validation code:', error);
      setSelfieValidationCode(null);
    } finally {
      setIsLoadingSelfieValidationCode(false);
    }
  };

  const getDocsByFlowVariant = () => {
    if (flowVariant === 'approved') {
      return {
        required: ['documentFront', 'documentBack', 'selfie'],
        optional: []
      };
    }

    return {
      required: ['paycheck', 'bankStatement'],
      optional: []
    };
  };

  const initializeDocuments = (uploadedTypes = new Set()) => {
    const { required: requiredDocs, optional: optionalDocs } = getDocsByFlowVariant();
    const initialDocuments = {};

    requiredDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        status: uploadedTypes.has(doc) ? 'uploaded' : 'pending', 
        optional: false 
      };
    });

    optionalDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        status: uploadedTypes.has(doc) ? 'uploaded' : 'pending', 
        optional: true 
      };
    });

    skipHydratedCompletionRef.current = requiredDocs.length > 0
      && requiredDocs.every((doc) => uploadedTypes.has(doc));

    setDocuments(initialDocuments);
    setIsDocumentsInitialized(true);
  };

  const handleFileInput = async (documentType, file) => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        [documentType]: 0
      }));

      const formData = new FormData();
      formData.append('files[]', file);
      formData.append('categories[]', documentType);

      const { customerId, requestId } = resolveCustomerRequestContext();
      
      const response = await api.post(
        `/customers/${customerId}/requests/${requestId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({
              ...prev,
              [documentType]: percentCompleted
            }));
          }
        }
      );

      // Limpiamos el progreso
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentType];
        return newProgress;
      });

      // Actualizamos el estado del documento
      setDocuments(prev => ({
        ...prev,
        [documentType]: {
          ...prev[documentType],
          status: 'uploaded'
        }
      }));

      // Track document uploaded
      const branchId = localStorage.getItem('temp_tokenBranchId');
      const customerDni = request?.customer?.dni;
      trackMobileDocumentUploaded({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        documentCategory: documentType,
        customerDni: customerDni,
        flowId: flowData?.flow_id
      });

      Swal.fire({
        title: '¡Éxito!',
        text: 'Documento subido correctamente',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });

    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentType];
        return newProgress;
      });
      
      Swal.fire({
        title: 'Error',
        text: 'No se pudo subir el documento',
        icon: 'error'
      });
    }
  };

  const areRequiredDocsUploaded = () => {
    return Object.values(documents).every(doc => 
      doc.optional || doc.status === 'uploaded'
    );
  };

  const renderDocumentCard = (docType, docInfo) => {
    const isUploaded = docInfo.status === 'uploaded';
    const isUploading = uploadProgress[docType] !== undefined;
    const progress = uploadProgress[docType] || 0;

    return (
      <div key={docType} className="mob-document-upload-container">
        {isUploaded ? (
          <div className="mob-document-uploaded">
            <FontAwesomeIcon icon={DOCUMENTS_MAP[docType]?.icon || faFileAlt} className="me-2" />
            {DOCUMENTS_MAP[docType]?.name || docType}
            {docInfo.optional && <span className="badge bg-secondary ms-2">Opcional</span>}
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          </div>
        ) : (
          <div className="mob-document-pending">
            <div className="mob-document-title">
              <FontAwesomeIcon icon={DOCUMENTS_MAP[docType]?.icon || faFileAlt} className="me-2" />
              {DOCUMENTS_MAP[docType]?.name || docType}
              {docInfo.optional && <span className="badge bg-secondary ms-2">Opcional</span>}
            </div>
            {isUploading ? (
              <div className="upload-progress">
                <div className="progress">
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {progress}%
                  </div>
                </div>
                <small className="text-muted mt-1">Subiendo documento...</small>
              </div>
            ) : (
              <div className="mob-document-actions">
                <input
                  type="file"
                  id={`camera-${docType}`}
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFileInput(docType, e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <input
                  type="file"
                  id={`gallery-${docType}`}
                  accept="image/*,application/pdf,.pdf"
                  onChange={(e) => handleFileInput(docType, e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-primary"
                  onClick={() => document.getElementById(`camera-${docType}`).click()}
                >
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Cámara
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => document.getElementById(`gallery-${docType}`).click()}
                >
                  <FontAwesomeIcon icon={faImages} className="me-2" />
                  Galería
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading || !isDocumentsInitialized) {
    return (
      <div className="document-upload-step bg-white p-4 rounded-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Preparando carga de documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="document-upload-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>{flowVariant === 'approved' ? 'Documentación final' : 'Subí tus documentos'}</h3>
        <p className="text-muted">
          {flowVariant === 'approved'
            ? (
              enableSelfieVerification
                ? 'Subí DNI frente, DNI dorso y una selfie sosteniendo el código de 6 dígitos mostrado.'
                : 'Subí DNI frente, DNI dorso y una selfie para completar la etapa final.'
            )
            : 'Subí los documentos que te pedimos para seguir con el proceso.'}
        </p>
      </div>

      {flowVariant === 'approved' && enableSelfieVerification && (
        <div className="alert alert-light-info d-flex align-items-center mb-4" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <div>
            {isLoadingSelfieValidationCode ? (
              <>Generando código de validación para la selfie...</>
            ) : selfieValidationCode ? (
              <>Código de validación para la selfie: <strong>{selfieValidationCode}</strong></>
            ) : (
              <>No se pudo obtener el código de validación de la selfie.</>
            )}
          </div>
        </div>
      )}

      {/* Información de la Solicitud */}
      {request && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Información de la Solicitud</h5>
            
            <div className="row g-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted d-block">Cliente</small>
                    <strong>{request.customer.name}</strong>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block">DNI</small>
                    <strong>{request.customer.dni}</strong>
                  </div>
                </div>
              </div>
              
              <div className="col-12">
                <hr className="my-2" />
              </div>
              
              <div className="col-6">
                <small className="text-muted d-block">Vendedor</small>
                <strong>{request.seller.full_name}</strong>
              </div>

              <div className="col-6 text-end">
                <small className="text-muted d-block">Sucursal</small>
                <strong>{request.branch.name}</strong>
              </div>

              <div className="col-12">
                <hr className="my-2" />
              </div>

              <div className="col-6 ms-auto text-end">
                <small className="text-muted d-block">Monto</small>
                <strong>{formatCurrency(request.amount, 2)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documentos Requeridos */}
      <h6 className="mb-3">
        Documentos obligatorios
        {areRequiredDocsUploaded() && (
          <span className="badge bg-success ms-2">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Listo
          </span>
        )}
      </h6>
      <div className="documents-grid mb-4">
        {Object.entries(documents)
          .filter(([_, doc]) => !doc.optional)
          .map(([docType, docInfo]) => renderDocumentCard(docType, docInfo))}
      </div>

      {/* Documentos Opcionales */}
      {Object.entries(documents).some(([_, doc]) => doc.optional) && (
        <>
          <h6 className="mb-3">Documentos opcionales</h6>
          <div className="documents-grid">
            {Object.entries(documents)
              .filter(([_, doc]) => doc.optional)
              .map(([docType, docInfo]) => renderDocumentCard(docType, docInfo))}
          </div>
        </>
      )}

      {/* Mensaje de progreso */}
      {areRequiredDocsUploaded() && (
        <div className="alert alert-success text-center mt-4">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <strong>¡Documentos completados!</strong>
          <br />
          <small className="text-muted">
            Avírequest a <strong>{request?.seller?.full_name}</strong> que has completado la carga de documentos.
          </small>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadStep;
